import { useState } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AutoPlusLogo } from '@/components/brand/autoplus-logo';
import { ThemeToggle } from '@/components/brand/theme-toggle';
import { BRAND_NAME } from '@/lib/brand';
import { VEHICLE_TEST_POINTS_TITLE } from '@shared/vehicle-test-points';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import {
  Linkedin,
  X,
  ArrowDownLeft,
  Facebook,
  Instagram,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { openCookieConsentBanner } from '@/lib/cookie-consent';
import { HoneypotWebsiteField } from '@/components/forms/honeypot-website-field';

export const footerLinkData = () => ({
  navigation: {
    product: [
      { name: 'جميع السيارات', href: '/listings' },
      { name: 'المزادات', href: '/listings?type=auction' },
      { name: 'السعر الثابت', href: '/listings?type=fixed' },
      { name: 'الميزات', href: '/#features' },
    ],
    company: [
      { name: 'من نحن', href: '/about' },
      { name: 'الأسئلة الشائعة', href: '/faqs' },
      { name: 'تقييم مجاني', href: '/evaluation' },
      { name: 'اتصل بنا', href: '/contact' },
      { name: 'المساعدة', href: '/help' },
    ],
    resources: [
      { name: 'كيف نفحص السيارات', href: '/how-we-test' },
      { name: VEHICLE_TEST_POINTS_TITLE, href: '/test-points' },
      { name: 'الدعم', href: '/support' },
      { name: 'سياسة الخصوصية', href: '/privacy' },
      { name: 'شروط الاستخدام', href: '/terms' },
    ],
    legal: [
      { name: 'الخصوصية', href: '/privacy' },
      { name: 'الشروط', href: '/terms' },
      { name: 'سياسة ملفات تعريف الارتباط', href: '/cookies' },
    ],
  },
  socialLinks: [
    { icon: X, label: 'X' },
    { icon: Facebook, label: 'Facebook' },
    { icon: Instagram, label: 'Instagram' },
    { icon: Linkedin, label: 'LinkedIn' },
  ],
  bottomLinks: [
    { href: '/privacy', label: 'سياسة الخصوصية' },
    { href: '/terms', label: 'شروط الاستخدام' },
    { href: '/cookies', label: 'سياسة ملفات تعريف الارتباط' },
  ],
});

const SECTION_LABELS = {
  product: 'السوق',
  company: 'الشركة',
  resources: 'الموارد',
  legal: 'قانوني',
} as const;

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState<string | null>(null);
  const [newsletterOk, setNewsletterOk] = useState(false);

  const subscribe = trpc.newsletter.subscribe.useMutation({
    onSuccess: (data) => {
      setNewsletterOk(true);
      setNewsletterMessage(data.message);
      toast.success(data.message);
      setEmail('');
    },
    onError: (err) => {
      setNewsletterOk(false);
      const msg = err.message || 'تعذّر إتمام الاشتراك. حاول لاحقاً.';
      setNewsletterMessage(msg);
      toast.error(msg);
    },
  });

  const submitNewsletter = () => {
    setNewsletterMessage(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setNewsletterOk(false);
      const msg = 'البريد الإلكتروني غير صحيح';
      setNewsletterMessage(msg);
      toast.error(msg);
      return;
    }
    subscribe.mutate({ email: trimmed, source: 'footer', companyWebsite });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitNewsletter();
  };

  return (
    <footer className="mt-20 w-full transition-colors duration-300">
      <div className="relative w-full px-5">
        <div className="container m-auto grid grid-cols-1 gap-12 py-12 md:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            <Link href="/" className="inline-flex items-center gap-3">
              <AutoPlusLogo size="header" />
            </Link>
            <p className="text-muted-foreground max-w-md">
              {BRAND_NAME} منصة موثوقة لبيع وشراء السيارات من خلال المزادات والأسعار الثابتة
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-2">
                {footerLinkData().socialLinks.map(({ icon: Icon, label }) => (
                  <Button
                    key={label}
                    type="button"
                    size="icon"
                    variant="outline"
                    aria-label={label}
                    onClick={() => toast.info('حسابات التواصل الاجتماعي غير متاحة بعد')}
                    className="hover:bg-primary dark:hover:bg-primary !border-primary/30 !hover:border-primary cursor-pointer shadow-none transition-all duration-500 hover:scale-110 hover:-rotate-12 hover:text-white hover:shadow-md"
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                ))}
              </div>
            </div>
            <form
              onSubmit={handleNewsletterSubmit}
              className="relative w-full max-w-md space-y-3"
            >
              <HoneypotWebsiteField
                id="footer-companyWebsite"
                value={companyWebsite}
                onChange={setCompanyWebsite}
              />
              <label htmlFor="footer-newsletter-email" className="block text-sm font-medium">
                اشترك في نشرتنا الإخبارية
              </label>
              <div className="relative w-full">
                <Input
                  type="email"
                  id="footer-newsletter-email"
                  name="email"
                  autoComplete="email"
                  placeholder="أدخل بريدك الإلكتروني"
                  className="h-12 w-full ps-28"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (newsletterMessage) setNewsletterMessage(null);
                  }}
                  disabled={subscribe.isPending}
                  required
                />
                <Button
                  type="submit"
                  disabled={subscribe.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    submitNewsletter();
                  }}
                  className="absolute top-1.5 start-1.5 z-10 min-w-[5.5rem] cursor-pointer transition-all duration-300 hover:px-6"
                >
                  {subscribe.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    'اشتراك'
                  )}
                </Button>
              </div>
              {newsletterMessage ? (
                <p
                  role="status"
                  className={cn(
                    'flex items-center gap-1.5 text-sm font-medium',
                    newsletterOk ? 'text-emerald-700' : 'text-destructive',
                  )}
                >
                  {newsletterOk ? <CheckCircle2 className="size-4 shrink-0" /> : null}
                  {newsletterMessage}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  احصل على آخر التحديثات والدروس والعروض الحصرية.
                </p>
              )}
            </form>
            <div className="pointer-events-none select-none pt-2" aria-hidden>
              <AutoPlusLogo
                size="header"
                className="h-20 w-auto max-w-[min(100%,20rem)] opacity-10 sm:h-24 lg:h-32 lg:max-w-xl"
              />
            </div>
          </div>

          <div className="grid w-full grid-cols-2 items-start justify-between gap-8 px-5 lg:col-span-3">
            {(['product', 'company', 'resources', 'legal'] as const).map(
              (section) => (
                <div key={section} className="w-full">
                  <h3 className="mb-4 flex w-fit items-center gap-2.5 text-sm font-semibold tracking-wider uppercase">
                    <span className="bg-primary h-3.5 w-0.5 shrink-0 rounded-sm" aria-hidden />
                    {SECTION_LABELS[section]}
                  </h3>
                  <ul className="space-y-3">
                    {footerLinkData().navigation[section].map((item) => (
                      <li key={item.name}>
                        <Link href={item.href} className="group text-muted-foreground hover:text-foreground decoration-primary -ms-5 inline-flex items-center gap-2 underline-offset-8 transition-all duration-500 hover:ps-5 hover:underline">
                          <ArrowDownLeft className="text-primary rotate-[225deg] opacity-30 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100 sm:group-hover:rotate-[225deg] md:rotate-0" />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            )}
          </div>
        </div>

        <div className="text-muted-foreground container m-auto flex flex-col items-center justify-between gap-4 p-4 text-xs md:flex-row md:px-0 md:text-sm">
          <p>
            &copy; {currentYear} {BRAND_NAME} | جميع الحقوق محفوظة
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <ThemeToggle variant="labeled" />
            <button
              type="button"
              onClick={openCookieConsentBanner}
              className="hover:text-foreground transition-colors duration-300"
            >
              إدارة تفضيلات الكوكيز
            </button>
            {footerLinkData().bottomLinks.map(({ href, label }) => (
              <Link key={href} href={href} className="hover:text-foreground transition-colors duration-300">
                {label}
              </Link>
            ))}
          </div>
        </div>
        <span className="from-primary/20 absolute inset-x-0 bottom-0 left-0 -z-10 h-1/3 w-full bg-gradient-to-t" />
      </div>
    </footer>
  );
}
